<script setup lang="ts">
import { useAddressStore } from '@/modules/shared/stores/address-store'
import { VPercentageField, VTaxIdField, VTextField } from '@/modules/shared/components'
import VSelectLocal from '../../../components/v-select.vue'

const props = defineProps<{
  form: any
  v$: any
  legalStructureOptions: { label: string; value: string }[]
  ownershipTypeOptions: { label: string; value: string }[]
  currencyOptions: { label: string; value: string }[]
}>()

const addressStore = useAddressStore()

const ownershipTypeDescriptions: Record<string, string> = {
  committed: 'Ownership will be calculated based on the commitment to the SPV',
  capital_called:
    'Ownership in the SPV and its assets is calculated by the capital called. This takes into account the fees paid, which might be different for each investor.',
  assigned: 'Allows you to manually assign an ownership % to each investor',
}

defineExpose({
  isValid: async () => {
    const fields = [
      'name',
      'legal_name',
      'date',
      'tax_id',
      'management_fee_percentage',
      'carried_interest',
      'preferred_return',
    ]
    const results = await Promise.all(fields.filter((f) => props.v$[f]).map((f) => props.v$[f].$validate()))
    return results.every(Boolean)
  },
})
</script>

<template>
  <div class="space-y-6">
    <div class="space-y-4">
      <VTextField
        v-model="form.legal_name"
        :appendText="form.is_venture360_providing_formation_documents ? ' A Series of V360 Holdings LLC' : null"
        label="Legal Name"
        :input-class="form.is_venture360_providing_formation_documents ? '!rounded-r-none !border-r-0' : ''"
        placeholder="Acme Corporation A Series of SPV Holdings LLC"
        property="legal_name"
        :v$="v$"
        class="flex-grow"
      />

      <VTextField v-model="form.name" label="Display Name" placeholder="Acme" property="name" :v$="v$" />

      <div class="my-8 h-px bg-gray-100"></div>

      <VSelectLocal
        v-model="form.type"
        label="Legal Structure"
        :options="legalStructureOptions"
        property="type"
        :v$="v$"
      />
      <VSelectLocal
        v-model="form.state_of_incorporation"
        label="State of Incorporation"
        :options="addressStore.stateOptions"
        property="state_of_incorporation"
        :v$="v$"
      />
      <VTaxIdField
        v-model="form.tax_id"
        label="EIN"
        format="ein"
        :description="form.is_venture360_the_administrator ? 'Venture360 will set up the EIN for you' : ''"
        property="tax_id"
        :v$="v$"
      />
      <VTextField v-model="form.date" type="date" label="Date Established" property="date" :v$="v$" />
    </div>

    <div class="my-8 h-px bg-gray-100"></div>

    <div class="space-y-4">
      <VSelectLocal
        v-model="form.ownership_type"
        label="How would you like to allocate the portfolio to investors?"
        :options="ownershipTypeOptions"
        property="ownership_type"
        :description="ownershipTypeDescriptions[form.ownership_type]"
        :v$="v$"
      />
    </div>

    <div class="my-8 h-px bg-gray-100"></div>

    <div class="space-y-4">
      <VSelectLocal v-model="form.currency" label="Currency" :options="currencyOptions" property="currency" :v$="v$" />
      <div class="grid gap-4 md:grid-cols-2">
        <VPercentageField
          v-model="form.management_fee_percentage"
          label="Management Fee %"
          :isPrecalculated="true"
          property="management_fee_percentage"
          :v$="v$"
        />
        <VPercentageField
          v-model="form.carried_interest"
          label="Carried Interest %"
          :isPrecalculated="true"
          property="carried_interest"
          :v$="v$"
        />
      </div>
      <div class="grid gap-4 md:grid-cols-2">
        <VPercentageField
          v-model="form.preferred_return"
          label="Preferred Return %"
          :isPrecalculated="true"
          property="preferred_return"
          :v$="v$"
        />
      </div>
    </div>
  </div>
</template>
